import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import {
  Modal,
  Button,
  Loading,
  MessageBanner,
} from '@folio/stripes/components';

const InactivityWarningModal = ({
  isOpen,
  onExtendSession,
  onExit,
  onViewLatestVersion,
}) => {
  const [modalState, setModalState] = useState('initial'); // initial, checking, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const [hasVersionConflict, setHasVersionConflict] = useState(false);

  const handleExtendSession = async () => {
    console.log('🔄 [InactivityWarningModal] Starting session extension check');
    setModalState('checking');

    try {
      const result = await onExtendSession();

      if (result.success) {
        console.log('✅ [InactivityWarningModal] Session extended successfully');
        setModalState('success');
        // Auto close after 2 seconds
        setTimeout(() => {
          setModalState('initial');
        }, 2000);
      } else {
        console.log('❌ [InactivityWarningModal] Session extension failed:', result.error);
        setErrorMessage(result.error);
        setHasVersionConflict(result.hasVersionConflict || false);
        setModalState('error');
      }
    } catch (error) {
      console.error('❌ [InactivityWarningModal] Extension check failed:', error);
      setErrorMessage('حدث خطأ أثناء التحقق من حالة التسجيلة');
      setModalState('error');
    }
  };

  const handleExit = () => {
    console.log('🚪 [InactivityWarningModal] User chose to exit');
    setModalState('initial');
    onExit();
  };

  const handleViewLatestVersion = () => {
    console.log('👀 [InactivityWarningModal] User wants to view latest version');
    onViewLatestVersion();
  };

  const renderInitialState = () => (
    <>
      <MessageBanner type="warning">
        <FormattedMessage
          id="ui-plugin-inventory-templates.inactivityWarning.message"
          defaultMessage="انتهت مهلة عدم النشاط. لم تعد التسجيلة محمية من التعديل المتزامن."
        />
      </MessageBanner>

      <div style={{ marginTop: '1rem', display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
        <Button onClick={handleExit}>
          <FormattedMessage
            id="ui-plugin-inventory-templates.inactivityWarning.exit"
            defaultMessage="الخروج"
          />
        </Button>
        <Button buttonStyle="primary" onClick={handleExtendSession}>
          <FormattedMessage
            id="ui-plugin-inventory-templates.inactivityWarning.extend"
            defaultMessage="تمديد الجلسة"
          />
        </Button>
      </div>
    </>
  );

  const renderCheckingState = () => (
    <div style={{ textAlign: 'center', padding: '2rem' }}>
      <Loading />
      <div style={{ marginTop: '1rem' }}>
        <FormattedMessage
          id="ui-plugin-inventory-templates.inactivityWarning.checking"
          defaultMessage="جاري التحقق من حالة التسجيلة..."
        />
      </div>
    </div>
  );

  const renderSuccessState = () => (
    <div style={{ textAlign: 'center', padding: '1rem' }}>
      <MessageBanner type="success">
        <FormattedMessage
          id="ui-plugin-inventory-templates.inactivityWarning.success"
          defaultMessage="تم تمديد الجلسة بنجاح"
        />
      </MessageBanner>
    </div>
  );

  const renderErrorState = () => (
    <>
      <MessageBanner type="error">
        {errorMessage}
      </MessageBanner>

      <div style={{ marginTop: '1rem', display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
        <Button onClick={handleExit}>
          <FormattedMessage
            id="ui-plugin-inventory-templates.inactivityWarning.exit"
            defaultMessage="الخروج"
          />
        </Button>
        {hasVersionConflict && (
          <Button buttonStyle="primary" onClick={handleViewLatestVersion}>
            <FormattedMessage
              id="ui-plugin-inventory-templates.inactivityWarning.viewLatest"
              defaultMessage="اطلاع على آخر إصدار"
            />
          </Button>
        )}
      </div>
    </>
  );

  const renderContent = () => {
    switch (modalState) {
      case 'checking':
        return renderCheckingState();
      case 'success':
        return renderSuccessState();
      case 'error':
        return renderErrorState();
      default:
        return renderInitialState();
    }
  };

  return (
    <Modal
      open={isOpen}
      label={
        <FormattedMessage
          id="ui-plugin-inventory-templates.inactivityWarning.title"
          defaultMessage="تحذير: انتهاء مهلة النشاط"
        />
      }
      dismissible={modalState === 'initial' || modalState === 'error'}
      onClose={modalState === 'success' ? undefined : handleExit}
      size="small"
    >
      {renderContent()}
    </Modal>
  );
};

InactivityWarningModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onExtendSession: PropTypes.func.isRequired,
  onExit: PropTypes.func.isRequired,
  onViewLatestVersion: PropTypes.func.isRequired,
};

export default InactivityWarningModal;
